/* eslint-disable react-hooks/exhaustive-deps */
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FastField, getIn } from "formik";
import { isEqual } from "lodash";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import AutoWidthPopper from "./AutoWidthPopper";
import configDefaultForm from "./configDefault";
import ErrorIcon from "./ErrorIcon";
import RequiredLabel from "./RequiredLabel";

const PAGE_SIZE = 20;
let loading = false;

const GlobitsPagingAutocomplete = (props, ref) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
        ref={ref}
      />
    )}
  </FastField>
);

const Component = forwardRef((props, ref) => {
  const {
    disabled,
    fullWidth = configDefaultForm.fullWidth,
    readOnly,
    name,
    size = configDefaultForm.size,
    variant = configDefaultForm.variant,
    className = "",
    getOptionLabel,
    displayLabel = "name",
    displayValue = "",
    getOptionSelected,
    displaySelected = "id",
    getOptionDisabled,
    noOptionsText = "Không có dữ liệu",
    includeNullOp,
    onChange,
    value,
    api,
    searchObject,
    clearOptionOnClose,
    allowLoadOptions = true,
    field,
    meta,
    setFieldValue,
    label,
    sortOptions,
    displayData,
    sx,
    required,
    requiredLabel,
    disableTyping = false,
    disableClearable = false,
    multiple,
    fillerOptions,
    handleResponseApi,
    onFocus,
    notDelay,
    autoWidthPopper,
    placeholder,
    freeSolo = false,
    tabIndex,
    onKeyDown,
    arrAdd = [],
    isSearchFreeSolo = false,
    endAdornment,
    InputProps,
    autoUpercase,
    delay = 500,
    isNotCallApi = false,
    checkLength = 0,
    // onKeyDown,
    ...otherProps
  } = props;

  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [totalPage, setTotalPage] = React.useState(1);
  const [open, setOpen] = React.useState(false);
  const [t, setT] = React.useState();

  useEffect(() => {
    if (!allowLoadOptions) {
      setOptions([]);
    }
  }, [allowLoadOptions]);

  useEffect(() => {
    if (open && allowLoadOptions) {
      if (checkLength) {
        // check độ dài
        if (keyword && keyword?.trim()?.length >= checkLength) {
          getData();
        }
      } else {
        getData();
      }
    }
  }, [keyword, open, searchObject]);

  const loadData = async (obj, first) => {
    if (!api || isNotCallApi) {
      return;
    }
    try {
      loading = true;
      const { data } = await api({
        pageSize: PAGE_SIZE,
        ...searchObject,
        ...obj,
        keyword: searchObject?.keyword ? searchObject?.keyword : keyword || "",
      });
      if (data?.content?.length > 0) {
        let newOptions = [...(!first ? options : []), ...data.content];
        if (sortOptions) {
          newOptions = sortOptions(newOptions);
        }
        if (fillerOptions) {
          newOptions = newOptions.filter(fillerOptions);
        }

        setOptions(newOptions);
        setTotalPage(data.totalPages);
      } else {
        setOptions([]);
        setTotalPage(0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      loading = false;
    }
  };

  const getData = () => {
    let newPage = 1;
    setPage(newPage);
    loadData({ pageIndex: newPage }, true);
  };

  const loadMoreResults = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadData({ pageIndex: nextPage });
  };

  const handleScroll = (event) => {
    if (loading) {
      return;
    }
    const listboxNode = event.currentTarget;

    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 2 && page < totalPage) {
      loadMoreResults();
    }
  };

  const onOpen = () => {
    //console.log("keyword", keyword);
    setKeyword("");
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    if (clearOptionOnClose) {
      setOptions([]);
      setTotalPage(1);
    }
  };

  const handleChangeText = (value) => {
    if (t) {
      clearTimeout(t);
    }
    setT(
      setTimeout(() => {
        setKeyword(value);
        if (typeof value === "string" && freeSolo && value.trim().length > 0) {
          setFieldValue(name, value || null);
        }
      }, delay)
    );
  };

  const disLabel = label ? (
    <RequiredLabel label={label} requiredLabel={requiredLabel || required} />
  ) : (
    ""
  );
  const isError = meta && meta.touched && meta.error;

  const getOptionLabelDefault = useMemo(
    () =>
      getOptionLabel
        ? getOptionLabel
        : (option) => {
          if (typeof option === "string") {
            if (autoUpercase) {
              return option?.toUpperCase();
            }
            return option;
          }
          const label = getIn(option, displayLabel);
          if (autoUpercase) {
            return label?.toUpperCase() ? String(label)?.toUpperCase() : "";
          }
          return label ? String(label) : "";
        },
    [getOptionLabel, displayLabel]
  );

  const getOptionSelectedDefault = useMemo(
    () =>
      getOptionSelected
        ? getOptionSelected
        : (option, value) => {
          if (!value) {
            return false;
          }
          return (
            getIn(option, displaySelected) === getIn(value, displaySelected)
          );
        },
    [getOptionSelected]
  );

  const handleChange = onChange
    ? onChange
    : (_, value) => {
      if (freeSolo) {
        setFieldValue(
          name,
          value
            ? displayValue
              ? getIn(value, displayValue)
              : getIn(value, displayLabel)
            : null
        );
      } else {
        setFieldValue(name, value ? value : null);
      }
    };

  return (
    <>
      {!fullWidth && disLabel && <label htmlFor={name}>{disLabel}</label>}
      <Autocomplete
        {...field}
        {...otherProps}
        id={name}
        name={name}
        disabled={disabled || readOnly}
        fullWidth={fullWidth}
        multiple={multiple}
        size={size}
        variant={variant}
        value={value || (field?.value ?? (multiple ? [] : null))}
        onChange={handleChange}
        options={useMemo(
          () =>
            includeNullOp
              ? [null, ...(options || [])]
              : options?.length > 0
                ? [...arrAdd, ...(options || [])]
                : [...(options || [])],
          [includeNullOp, options]
        )}
        getOptionLabel={getOptionLabelDefault}
        getOptionSelected={getOptionSelectedDefault}
        getOptionDisabled={getOptionDisabled}
        noOptionsText={noOptionsText}
        onOpen={onOpen}
        open={open}
        onBlur={() => {
          if (
            isSearchFreeSolo &&
            !multiple &&
            !options?.find(
              (option) =>
                option[displaySelected] === field?.value?.[displaySelected]
            )
          ) {
            setFieldValue(name, field?.value);
          }
        }}
        onClose={onClose}
        autoHighlight
        openOnFocus
        freeSolo={freeSolo}
        PopperComponent={autoWidthPopper ? AutoWidthPopper : undefined}
        disableClearable={disableClearable}
        onKeyDown={(event) => {
          if (onKeyDown) {
            onKeyDown(event, options, setFieldValue, name);
          } else {
            if (event.key === "Enter") {
              event.stopPropagation();
              event.preventDefault();
              if (isSearchFreeSolo) {
                setFieldValue(name, keyword);
              }
              return true;
            }
            return !!onKeyDown ? onKeyDown(event) : true;
          }
        }}
        onInputChange={(event, newValue, reason) => {
          if (reason === "input" && isSearchFreeSolo) {
            setKeyword(newValue || "");
            handleChangeText(newValue);
            setFieldValue(name, newValue);
          } else if (reason === "reset" && isSearchFreeSolo) {
            setKeyword(field?.value || "");
          } else if (reason === "clear" && isSearchFreeSolo) {
            setKeyword("");
            setFieldValue(name, null);
          } else {
            handleChangeText(event?.target?.value);
          }
        }}
        {...(isSearchFreeSolo ? { inputValue: keyword } : {})}
        renderInput={(params) => (
          <TextField
            {...params}
            label={fullWidth && disLabel ? disLabel : null}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off", // disable autocomplete and autofill
              readOnly: readOnly || disableTyping,
              placeholder: placeholder,
              tabIndex: tabIndex,
            }}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {endAdornment}
                </>
              ),
            }}
            InputLabelProps={{
              htmlFor: name,
            }}
            error={isError}
            inputRef={ref}
            helperText={fullWidth && isError ? meta.error : ""}
            className={`input-container ${className} ${readOnly ? "read-only-autocomplete" : ""
              }`}
            autoFocus={onFocus || false}
          />
        )}
        ListboxProps={{
          onScroll: handleScroll,
        }}
      />
      {isError && !fullWidth && <ErrorIcon helperText={meta.error} />}
    </>
  );
});

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps.name !== currentProps.name ||
  nextProps.value !== currentProps.value ||
  nextProps.handleChange !== currentProps.handleChange ||
  nextProps.onChange !== currentProps.onChange ||
  nextProps.getOptionDisabled !== currentProps.getOptionDisabled ||
  nextProps.label !== currentProps.label ||
  nextProps.required !== currentProps.required ||
  nextProps.api !== currentProps.api ||
  nextProps.disabled !== currentProps.disabled ||
  nextProps.readOnly !== currentProps.readOnly ||
  !isEqual(nextProps.searchObject, currentProps.searchObject) ||
  nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
  Object.keys(nextProps).length !== Object.keys(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
  getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
  getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
  getIn(currentProps.formik.touched, currentProps.name);

export default React.memo(forwardRef(GlobitsPagingAutocomplete));
